// CB:BEGIN contractor-realtor-quotes-ui
/**
 * ContractorRealtorQuotes
 *
 * Lists open realtor quote requests within contractor's service area.
 * Fetches GET /api/contractor/realtor-quotes.
 *
 * Features:
 *  - "Set up service area" banner if contractor has no coords set
 *  - Distance badge per quote request
 *  - "Bid slots remaining" indicator
 *  - My bid status badge if already submitted
 *  - Navigate to bid form on click
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../lib/useApi';

// ── Types ─────────────────────────────────────────────────────────────────────

interface RealtorQuoteItem {
  id: string;
  propertyAddressLine1: string;
  propertyCity?: string | null;
  propertyState?: string | null;
  propertyZip?: string | null;
  status: string;
  maxBids: number;
  bidCount: number;
  expiresAt: string;
  createdAt: string;
  distanceMiles?: number | null;
  contractorRadiusMiles?: number | null;
  myBidStatus?: string | null;
  myBidId?: string | null;
}

interface Props {
  onNavigate?: (screen: string) => void;
  onNavigateToSetup?: () => void;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ContractorRealtorQuotes({ onNavigate, onNavigateToSetup, className = '' }: Props) {
  const { get } = useApi();

  const [quotes, setQuotes] = useState<RealtorQuoteItem[]>([]);
  const [contractorRadiusMiles, setContractorRadiusMiles] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: any = await get('/api/contractor/realtor-quotes');
      setQuotes(Array.isArray(data?.requests) ? data.requests : []);
      setContractorRadiusMiles(data?.contractorRadiusMiles ?? null);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load realtor quote requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className={`space-y-3 ${className}`}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse h-20 bg-gray-100 rounded-xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 ${className}`}>
        {error}
      </div>
    );
  }

  return (
    <div className={className} data-testid="contractor-realtor-quotes">
      {/* Radius context label */}
      {contractorRadiusMiles != null && quotes.length > 0 && (
        <div className="mb-3 flex items-center gap-2 text-xs text-gray-500">
          <svg className="w-4 h-4 text-purple-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          Showing realtor quote requests within <strong className="mx-1">{contractorRadiusMiles} mi</strong> of your base
        </div>
      )}

      {quotes.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center">
          <div className="text-gray-400 mb-2">
            <svg className="w-10 h-10 mx-auto" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-700">No realtor quote requests in your area</p>
          <p className="text-xs text-gray-400 mt-1">
            Realtors post quote requests here when they need bids for client properties.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {quotes.map((quote) => (
            <QuoteCard
              key={quote.id}
              quote={quote}
              onClick={() => onNavigate?.(`/contractor/realtor-quotes/${quote.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Quote Card ─────────────────────────────────────────────────────────────────

function QuoteCard({
  quote,
  onClick,
}: {
  quote: RealtorQuoteItem;
  onClick: () => void;
}) {
  const slotsLeft = quote.maxBids - quote.bidCount;
  const expiresAt = new Date(quote.expiresAt);
  const isExpiringSoon = expiresAt.getTime() - Date.now() < 24 * 60 * 60 * 1000;
  const hasDist = quote.distanceMiles != null;
  const hasBid = !!quote.myBidStatus;

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-4 hover:border-purple-300 hover:shadow-sm transition-all cursor-pointer"
      onClick={onClick}
      data-testid="realtor-quote-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-gray-900 truncate">{quote.propertyAddressLine1}</p>
            {hasBid && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                quote.myBidStatus === 'SUBMITTED'
                  ? 'bg-purple-100 text-purple-700'
                  : quote.myBidStatus === 'ACCEPTED'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {quote.myBidStatus === 'SUBMITTED' ? 'Bid submitted' : quote.myBidStatus}
              </span>
            )}
          </div>
          {(quote.propertyCity || quote.propertyState) && (
            <p className="text-xs text-gray-500 mt-0.5">
              {[quote.propertyCity, quote.propertyState].filter(Boolean).join(', ')}
            </p>
          )}
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-xs font-medium ${slotsLeft <= 1 ? 'text-red-500' : 'text-gray-500'}`}>
              {slotsLeft} bid slot{slotsLeft !== 1 ? 's' : ''} remaining
            </span>
            {isExpiringSoon && (
              <span className="text-xs text-amber-600 font-medium">Expires soon</span>
            )}
          </div>
        </div>

        {hasDist && (
          <div className="shrink-0 text-right">
            <div className="text-sm font-semibold text-purple-600">{quote.distanceMiles} mi</div>
            {quote.contractorRadiusMiles && (
              <div className="text-xs text-gray-400">within {quote.contractorRadiusMiles} mi</div>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs font-medium text-purple-600 hover:underline">
          {hasBid ? 'View your bid →' : 'View & bid →'}
        </span>
        <span className="text-xs text-gray-400">
          Expires {expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      </div>
    </div>
  );
}

export default ContractorRealtorQuotes;
// CB:END contractor-realtor-quotes-ui
